import { useTranslation } from 'react-i18next';
import { Typography } from 'app/component-library-wave';
import { ActiveCustomerLocationPage } from '../../../page-wrapper';
import { ContainerFixed } from 'app/components/container';
import { useAppNavigation } from 'app/utils/navigation-utils';
import { useVoipSettings, validatePhoneNumber } from '../voip-common';
import { Card } from 'app/components/card';
import { Button, RadioGroup, RadioGroupItem, Spinner, TextField } from '@lyse-as/formds-react';
import { navigationService } from 'app/service/navigation/navigation-service';
import { formatVoipNumber } from 'app/utils/string-util';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'app/hooks/redux-thunk';
import { updateVoipSettings } from 'app/store/actions/voip-settings-thunks';
import { FetchStatus } from 'app/store/root-types';
import { resetVoipUpdateStatus } from 'app/store/actions/voip-settings-actions';
import styles from '../voip-common.module.scss';
import { VoipContainer } from './container';

type ForwardFrom = 'all' | 'specific';
type Condition = 'always' | 'busy' | 'lost';

export const ForwardingNewRule: ActiveCustomerLocationPage = ({ userContext }) => {
  const { t } = useTranslation();
  const { goToPath } = useAppNavigation();
  const dispatch = useAppDispatch();
  const { selectedPhoneNumber: selectedSettings, settings, updateFetchStatus } = useVoipSettings();
  const [forwardFrom, setForwardFrom] = useState<ForwardFrom>('all');
  const [condition, setCondition] = useState<Condition>('always');
  const [toggleRenderFlag, setToggleRenderFlag] = useState(false);
  const toNumberRef = useRef<HTMLInputElement>(null);
  const fromNumberRef = useRef<HTMLInputElement>(null);
  const [submitSettings, setSubmitSettings] = useState(false);

  const isLoading = updateFetchStatus === FetchStatus.PENDING;

  useEffect(() => {
    if (isFromNumberValid && isToNumberValid && submitSettings) {
      submitChanges();
      setSubmitSettings(false);
    }
  }, [submitSettings, toggleRenderFlag]);

  useEffect(() => {
    if (isLoading) {
      dispatch(resetVoipUpdateStatus());
      goToPath(navigationService.PAGES.voipSettingsForwarding.url);
    }
  }, [updateFetchStatus]);

  const isFromNumberValid =
    forwardFrom === 'all' ? true : fromNumberRef.current?.value && validatePhoneNumber(fromNumberRef.current.value);
  const isToNumberValid = toNumberRef.current?.value && validatePhoneNumber(toNumberRef.current.value);

  if (!selectedSettings) {
    return null;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isLoading) {
      setSubmitSettings((current) => !current);
    }
  };

  const submitChanges = () => {
    const newForwardRule: MinesiderBackend.ForwardRule = {
      forwardTo: toNumberRef.current?.value,
      trigger: forwardFrom === 'all' ? 'always' : 'caller',
      ...(fromNumberRef.current?.value && { callers: [fromNumberRef.current.value] }),
    };

    const currentSettings = settings[selectedSettings].settings;

    const newForwardingRules = currentSettings.forwarding?.rules?.concat(newForwardRule);
    const newSettings = {
      ...currentSettings,
      forwarding: {
        ...currentSettings.forwarding,
        rules: newForwardingRules,
      },
    };
    dispatch(updateVoipSettings(newSettings));
  };

  const onFromChange = (value: string) => {
    setForwardFrom(value as ForwardFrom);
  };

  const onConditionChange = (value: string) => {
    setCondition(value as Condition);
  };

  return (
    <VoipContainer services={userContext.services}>
      <div className={styles.container}>
        <ContainerFixed className={styles.tabbedContainer}>
          <form onSubmit={(e) => handleSubmit(e)}>
            <ContainerFixed isNarrow={true} className={styles.section}>
              <Typography
                component="h5"
                variant="formds-altibox-subtitle-secondary"
                className={styles.header}
                bold={true}
              >
                {t('pages.voip.forwarding.rules.title', { voipNumber: formatVoipNumber(selectedSettings) })}
              </Typography>
              <Card containerClassName={styles.card} className={styles.card}>
                <Typography component="h6" variant="formds-lead" bold={true}>
                  {t('pages.voip.forwarding.rules.forwardFrom')}
                </Typography>
                <RadioGroup
                  aria-label={t('pages.voip.forwarding.rules.forwardFrom')}
                  orientation="vertical"
                  onValueChange={onFromChange}
                  defaultValue={forwardFrom}
                >
                  <RadioGroupItem value="all">{t('pages.voip.forwarding.rules.filter.all')}</RadioGroupItem>
                  <RadioGroupItem value="specific">{t('pages.voip.forwarding.rules.filter.specific')}</RadioGroupItem>
                </RadioGroup>
                {forwardFrom === 'specific' && (
                  <TextField
                    label={t('pages.voip.forwarding.rules.phoneNumber')}
                    name="triggerNumber"
                    required={true}
                    type="tel"
                    ref={fromNumberRef}
                    scheme={isFromNumberValid ? undefined : 'error'}
                  />
                )}
              </Card>
              <Card containerClassName={styles.card} className={styles.card}>
                <Typography component="h6" variant="formds-lead" bold={true}>
                  {t('pages.voip.forwarding.rules.forwardCondition')}
                </Typography>
                <RadioGroup
                  aria-label={t('pages.voip.forwarding.rules.forwardCondition')}
                  orientation="vertical"
                  onValueChange={onConditionChange}
                  defaultValue={condition}
                >
                  <RadioGroupItem value="always">{t('pages.voip.forwarding.rules.conditions.always')}</RadioGroupItem>
                  <RadioGroupItem value="lost">{t('pages.voip.forwarding.rules.conditions.lost')}</RadioGroupItem>
                  <RadioGroupItem value="busy">{t('pages.voip.forwarding.rules.conditions.busy')}</RadioGroupItem>
                </RadioGroup>
              </Card>
              <Card containerClassName={styles.card} className={styles.card}>
                <TextField
                  label={t('pages.voip.forwarding.rules.forwardToNumber')}
                  name="triggerNumber"
                  description={t('pages.voip.forwarding.rules.forwardToNumberDescription')}
                  required={true}
                  type="tel"
                  ref={toNumberRef}
                  onBlur={() => setToggleRenderFlag((current) => !current)}
                  scheme={isToNumberValid ? undefined : 'error'}
                />
              </Card>
              <div className={styles.buttonRow}>
                <Button
                  variant="outlined"
                  scheme="primary"
                  onClick={() => goToPath(navigationService.PAGES.voipSettingsForwarding.url)}
                >
                  {t('pages.voip.forwarding.cta.cancel')}
                </Button>
                <Button
                  variant="filled"
                  scheme="primary"
                  type="submit"
                  disabled={updateFetchStatus === FetchStatus.PENDING}
                >
                  {updateFetchStatus === FetchStatus.PENDING ? (
                    <span style={{ display: 'inline-flex', width: '1.25em', height: '1.25em' }}>
                      <Spinner loadingText={'Laster'} loadingCompleteText={'Ferdig'} isLoadingComplete={false} />
                    </span>
                  ) : (
                    <>{t('pages.voip.forwarding.cta.create')}</>
                  )}
                </Button>
              </div>
            </ContainerFixed>
          </form>
        </ContainerFixed>
      </div>
    </VoipContainer>
  );
};
